import React, { useState, useEffect } from "react";
import { Download, RefreshCw, X, CheckCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePWAUpdate } from "@shared/hooks/usePWAUpdate";
import {
  shouldShowPWAUpdates,
  detectMobile,
} from "../../../../../shared/utils/mobileDetection";
import { PWAInstallationManager } from "../../../../../shared/utils/pwaInstallationContext";

interface PWAUpdateManagerProps {
  onUpdateAvailable?: (registration: ServiceWorkerRegistration) => void;
  onUpdateInstalled?: () => void;
}

const PWAUpdateManager: React.FC<PWAUpdateManagerProps> = ({
  onUpdateAvailable,
  onUpdateInstalled,
}) => {
  const {
    updateStatus,
    installUpdate: installPWAUpdate,
    isUpdateAvailable,
    isUpdateReady,
    isInstalling,
  } = usePWAUpdate();

  const [isVisible, setIsVisible] = useState(false);
  const [dismissedUpdate, setDismissedUpdate] = useState<string | null>(null);
  const [installProgress, setInstallProgress] = useState(0);
  const [installStep, setInstallStep] = useState("Preparing...");
  // True from the moment Install is pressed until the service reloads (or the attempt
  // fails). The progress modal owns the whole UX during this window.
  const [installFlow, setInstallFlow] = useState(false);
  const installFlowRef = React.useRef(false);
  const [mobileInfo, setMobileInfo] = useState(() => detectMobile());
  const [shouldShow, setShouldShow] = useState(false);

  // Detect mobile platform and determine if PWA updates should be shown
  useEffect(() => {
    const detection = detectMobile();
    setMobileInfo(detection);

    // Only show PWA updates if:
    // 1. We're on a mobile device
    // 2. This is the app the user installed (or no installation context exists)
    const shouldShowUpdates = shouldShowPWAUpdates();
    const installationContext = PWAInstallationManager.getInstallationContext();
    const currentDomain = window.location.hostname;

    let shouldShowForThisApp = shouldShowUpdates;

    if (installationContext) {
      // Subdomain-aware update logic
      // User has installed a specific app - show updates based on installation context:
      const isWhitelabelDomain =
        (currentDomain.endsWith(".zurilink.com") &&
          currentDomain !== "zurilink.com") ||
        currentDomain.includes("localhost:3001");

      shouldShowForThisApp =
        shouldShowUpdates &&
        (installationContext.installationDomain === currentDomain || // Same domain
          (installationContext.isMainApp && isWhitelabelDomain) || // Main app user accessing whitelabel
          (!installationContext.isMainApp && isWhitelabelDomain)); // Whitelabel user (any affiliation) accessing whitelabel
    }

    setShouldShow(shouldShowForThisApp);
  }, []);

  // Show notification when update becomes available
  useEffect(() => {
    // Only proceed if we should show PWA updates on this platform and app
    if (!shouldShow) {
      return;
    }

    // Check if user recently dismissed or initiated refresh
    // Use domain-specific keys to prevent cross-domain conflicts
    const appDomain = window.location.hostname.includes("localhost")
      ? "whitelabel-dev"
      : "whitelabel-prod";
    const updateDismissed = localStorage.getItem(
      `pwa-update-dismissed-${appDomain}`
    );
    const refreshInitiated = localStorage.getItem(
      `pwa-refresh-initiated-${appDomain}`
    );
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;

    // Don't show if recently dismissed or refresh was initiated
    if (updateDismissed || refreshInitiated) {
      const dismissedTime = updateDismissed ? parseInt(updateDismissed) : 0;
      const refreshTime = refreshInitiated ? parseInt(refreshInitiated) : 0;
      const mostRecentTime = Math.max(dismissedTime, refreshTime);

      if (now - mostRecentTime < oneHour) {
        return; // Don't show for 1 hour after dismissal or refresh
      } else {
        // Clear old dismissal states for this domain
        localStorage.removeItem(`pwa-update-dismissed-${appDomain}`);
        localStorage.removeItem(`pwa-refresh-initiated-${appDomain}`);
      }
    }

    if (isUpdateAvailable && !dismissedUpdate) {
      setIsVisible(true);
    }

    // iOS-specific: Force visibility if no updates detected but app might be stale
    if (mobileInfo.isIOS && !isUpdateAvailable && !dismissedUpdate) {
      const lastForceCheck = localStorage.getItem(
        `last-ios-force-check-${appDomain}`
      );
      const twoHours = 2 * 60 * 60 * 1000;

      if (!lastForceCheck || now - parseInt(lastForceCheck) > twoHours) {
        localStorage.setItem(
          `last-ios-force-check-${appDomain}`,
          now.toString()
        );

        // Show update prompt after a delay
        setTimeout(() => {
          setIsVisible(true);
        }, 8000); // Show after 8 seconds - better engagement window
      }
    }
  }, [isUpdateAvailable, dismissedUpdate, mobileInfo.isIOS, shouldShow]);

  // Update ready = the new worker has actually taken control (the service reloads ~0.7s
  // later). During a button-driven install the progress modal owns the UX — just complete
  // the bar; otherwise show the brief "Update Ready" card.
  useEffect(() => {
    if (isUpdateReady) {
      if (installFlowRef.current) {
        setInstallProgress(100);
        setInstallStep("Update installed");
        onUpdateInstalled?.();
        return;
      }
      setIsVisible(true);

      // Hide notification after update is ready and give user time to see it
      setTimeout(() => {
        setIsVisible(false);
        // Clear dismissal states to allow future updates
        const appDomain = window.location.hostname.includes("localhost")
          ? "whitelabel-dev"
          : "whitelabel-prod";
        localStorage.removeItem(`pwa-update-dismissed-${appDomain}`);
        localStorage.removeItem(`pwa-refresh-initiated-${appDomain}`);
        setDismissedUpdate(null);
      }, 3000); // Hide after 3 seconds

      onUpdateInstalled?.();
    }
  }, [isUpdateReady, onUpdateInstalled]);

  // Notify parent when update becomes available
  useEffect(() => {
    if (isUpdateAvailable && updateStatus.registration) {
      onUpdateAvailable?.(updateStatus.registration);
    }
  }, [isUpdateAvailable, updateStatus.registration, onUpdateAvailable]);

  const handleDismiss = () => {
    setIsVisible(false);
    setDismissedUpdate(updateStatus.buildInfo?.version || "dismissed");

    // Record dismissal with timestamp
    const appDomain = window.location.hostname.includes("localhost")
      ? "whitelabel-dev"
      : "whitelabel-prod";
    localStorage.setItem(
      `pwa-update-dismissed-${appDomain}`,
      Date.now().toString()
    );

    // Auto-show again after 3 hours if update is still available (industry standard)
    setTimeout(() => {
      if (isUpdateAvailable) {
        setIsVisible(true);
        setDismissedUpdate(null);
      }
    }, 3 * 60 * 60 * 1000); // 3 hours - less aggressive, better UX
  };

  const handleInstall = async () => {
    installFlowRef.current = true;
    setInstallFlow(true);
    setIsVisible(false);
    setInstallProgress(6);
    setInstallStep("Preparing update...");

    // Honest progress: creep (fast early, slow later, capped at 85%) while the new worker
    // actually downloads/installs. The 100% ✓ comes only when installUpdate resolves —
    // which now means the new worker has really taken control; the service reloads ~0.7s
    // after that. No fake sprint-to-90, no component-side reload race.
    const started = Date.now();
    const creep = setInterval(() => {
      const elapsed = Date.now() - started;
      if (elapsed > 12000) setInstallStep("Still downloading — large update...");
      else if (elapsed > 1500) setInstallStep("Downloading update...");
      setInstallProgress((p) => Math.min(85, p + (p < 40 ? 6 : 1)));
    }, 700);

    const appDomain = window.location.hostname.includes("localhost")
      ? "whitelabel-dev"
      : "whitelabel-prod";
    localStorage.setItem(
      `pwa-refresh-initiated-${appDomain}`,
      Date.now().toString()
    );

    try {
      await installPWAUpdate(); // resolves at real activation; service reloads after
      clearInterval(creep);
      setInstallProgress(100);
      setInstallStep("Update installed");
    } catch (error) {
      clearInterval(creep);
      console.error("Failed to install update:", error);
      installFlowRef.current = false;
      setInstallFlow(false);
      setInstallProgress(0);
      setIsVisible(true); // re-offer the update card
    }
  };

  const handleRefresh = () => {
    setIsVisible(false);
    // Record refresh initiation
    const appDomain = window.location.hostname.includes("localhost")
      ? "whitelabel-dev"
      : "whitelabel-prod";
    localStorage.setItem(
      `pwa-refresh-initiated-${appDomain}`,
      Date.now().toString()
    );
    // The update is already active (controllerchange) — just reload, immediately.
    setTimeout(() => window.location.reload(), 300);
  };

  // Don't render anything if we shouldn't show updates
  if (!shouldShow) {
    return null;
  }

  return (
    <>
      {/* Update Available Notification */}
      {isVisible && isUpdateAvailable && !isUpdateReady && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4 animate-in slide-in-from-bottom-2">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Download className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Update Available</h3>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            A new version of the app is available. Install now to get the latest
            features and improvements.
          </p>

          <div className="flex space-x-2">
            <Button
              onClick={handleInstall}
              disabled={isInstalling}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isInstalling ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Installing...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Install Update
                </>
              )}
            </Button>
            <Button onClick={handleDismiss} variant="outline" className="px-3">
              Later
            </Button>
          </div>
        </div>
      )}

      {/* Update Ready Notification */}
      {isVisible && isUpdateReady && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full bg-white border border-green-200 rounded-lg shadow-lg p-4 animate-in slide-in-from-bottom-2">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <h3 className="font-semibold text-gray-900">Update Ready</h3>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            The update has been installed successfully. Refresh the app to apply
            the changes.
          </p>

          <div className="flex space-x-2">
            <Button
              onClick={handleRefresh}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Refresh Now
            </Button>
            <Button onClick={handleDismiss} variant="outline" className="px-3">
              Later
            </Button>
          </div>
        </div>
      )}

      {/* Installation Progress Modal — owns the UX from Install press to reload */}
      {installFlow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              {installProgress >= 100 ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <RefreshCw className="h-6 w-6 text-blue-600 animate-spin" />
              )}
              <h3 className="font-semibold text-gray-900">
                {installProgress >= 100 ? "Update installed" : "Installing Update"}
              </h3>
            </div>

            <div className="mb-1">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>
                  {installProgress >= 100 ? "Reloading with the new version…" : installStep}
                </span>
                <span>{installProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    installProgress >= 100 ? "bg-green-600" : "bg-blue-600"
                  }`}
                  style={{ width: `${installProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAUpdateManager;
